import { useState, useEffect } from 'react';
import api from '@/api';
import { Users, Plus, Loader2, Pencil, Trash2, Shield, ShieldOff, Key, Building2, Globe, Lock, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const ROLE_COLORS = {
  master_admin: { bg: 'var(--primary-muted)', text: 'var(--primary)', border: 'var(--primary)' },
  admin: { bg: 'var(--warning-muted)', text: 'var(--warning)', border: 'var(--warning)' },
  operator: { bg: 'var(--info-muted)', text: 'var(--info)', border: 'var(--info)' },
  customer: { bg: 'var(--success-muted)', text: 'var(--success)', border: 'var(--success)' },
  sales_admin: { bg: 'rgba(217, 70, 239, 0.18)', text: '#D946EF', border: '#D946EF' },
  sales: { bg: 'rgba(168, 85, 247, 0.15)', text: '#A855F7', border: '#A855F7' },
  accounting: { bg: 'rgba(20, 184, 166, 0.15)', text: '#14B8A6', border: '#14B8A6' },
  moderator: { bg: 'rgba(6, 182, 212, 0.15)', text: '#06B6D4', border: '#06B6D4' },
  vendor: { bg: 'rgba(236, 72, 153, 0.15)', text: '#EC4899', border: '#EC4899' },
};

const ACCESS_SCOPE_BADGES = {
  ALL: { bg: 'rgba(16, 185, 129, 0.15)', text: '#10B981', icon: Globe, label: 'ALL TENANTS' },
  ASSIGNED: { bg: 'rgba(251, 191, 36, 0.15)', text: '#FBBF24', icon: Lock, label: 'ASSIGNED' },
};

const ROLES = ['master_admin', 'admin', 'sales_admin', 'sales', 'accounting', 'moderator', 'operator', 'customer', 'vendor'];
const INTERNAL_ROLES = ['master_admin', 'admin', 'sales_admin', 'sales', 'accounting', 'moderator', 'operator'];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', role: 'operator', customer_id: '', vendor_id: '', access_scope: 'ASSIGNED', allowed_customer_ids: [] });
  const [editForm, setEditForm] = useState({ full_name: '', role: '', is_active: true, customer_id: '', vendor_id: '', new_password: '', access_scope: 'ASSIGNED', allowed_customer_ids: [] });
  const [saving, setSaving] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [bulkSelectedUsers, setBulkSelectedUsers] = useState([]);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkCustomers, setBulkCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, cRes, meRes, vRes] = await Promise.all([
        api.get('/users'),
        api.get('/customers', { params: { limit: 200 } }),
        api.get('/auth/me'),
        api.get('/vendors', { params: { limit: 500 } }).catch(() => ({ data: { items: [] } })),
      ]);
      setUsers(uRes.data);
      setCustomers(cRes.data.items || []);
      setCurrentUser(meRes.data);
      setVendors(vRes.data.items || vRes.data || []);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const isMasterAdmin = currentUser?.role === 'master_admin';

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        customer_id: form.customer_id || undefined,
        vendor_id: form.role === 'vendor' ? (form.vendor_id || undefined) : undefined,
        access_scope: INTERNAL_ROLES.includes(form.role) ? form.access_scope : undefined,
        allowed_customer_ids: form.access_scope === 'ASSIGNED' ? form.allowed_customer_ids : [],
      };
      if (form.role === 'vendor' && !form.vendor_id) {
        toast.error('Pick a shop/vendor for this vendor user');
        setSaving(false);
        return;
      }
      await api.post('/users', payload);
      toast.success('User created!');
      setShowCreate(false);
      setForm({ email: '', password: '', full_name: '', role: 'operator', customer_id: '', vendor_id: '', access_scope: 'ASSIGNED', allowed_customer_ids: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      full_name: user.full_name || '',
      role: user.role || 'operator',
      is_active: user.is_active !== false,
      customer_id: user.customer_id || '',
      vendor_id: user.vendor_id || '',
      new_password: '',
      access_scope: user.access_scope || 'ASSIGNED',
      allowed_customer_ids: user.allowed_customer_ids || [],
    });
    setShowPasswordField(false);
    setShowEdit(true);
  };

  const openAssign = (user) => {
    setSelectedUser(user);
    setEditForm({
      ...editForm,
      access_scope: user.access_scope || 'ASSIGNED',
      allowed_customer_ids: user.allowed_customer_ids || [],
    });
    setShowAssign(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        full_name: editForm.full_name,
        role: editForm.role,
        is_active: editForm.is_active,
        customer_id: editForm.role === 'customer' ? editForm.customer_id : null,
        vendor_id: editForm.role === 'vendor' ? (editForm.vendor_id || null) : null,
      };
      
      // Include access_scope for internal roles
      if (INTERNAL_ROLES.includes(editForm.role)) {
        payload.access_scope = editForm.access_scope;
        payload.allowed_customer_ids = editForm.access_scope === 'ASSIGNED' ? editForm.allowed_customer_ids : [];
      }
      
      if (editForm.new_password && editForm.new_password.length > 0) {
        if (editForm.new_password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setSaving(false);
          return;
        }
        payload.new_password = editForm.new_password;
      }
      
      await api.put(`/users/${selectedUser.id}`, payload);
      toast.success(editForm.new_password ? 'User updated & password reset!' : 'User updated!');
      setShowEdit(false);
      setSelectedUser(null);
      setShowPasswordField(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/users/${selectedUser.id}`, {
        access_scope: editForm.access_scope,
        allowed_customer_ids: editForm.access_scope === 'ASSIGNED' ? editForm.allowed_customer_ids : [],
      });
      toast.success('Customer access updated!');
      setShowAssign(false);
      setSelectedUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update access');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkAssign = async () => {
    if (bulkSelectedUsers.length === 0 || bulkCustomers.length === 0) {
      toast.error('Select users and customers');
      return;
    }
    setSaving(true);
    try {
      for (const userId of bulkSelectedUsers) {
        const user = users.find(u => u.id === userId);
        if (user && INTERNAL_ROLES.includes(user.role)) {
          const existingCustomers = user.allowed_customer_ids || [];
          const newCustomers = [...new Set([...existingCustomers, ...bulkCustomers])];
          await api.put(`/users/${userId}`, {
            allowed_customer_ids: newCustomers,
          });
        }
      }
      toast.success(`Updated ${bulkSelectedUsers.length} users`);
      setShowBulkAssign(false);
      setBulkSelectedUsers([]);
      setBulkCustomers([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (user) => {
    setSelectedUser(user);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User deactivated!');
      setShowDelete(false);
      setSelectedUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      load();
    } catch (e) {
      toast.error('Failed to update user status');
    }
  };

  const toggleBulkSelect = (userId) => {
    setBulkSelectedUsers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const getRoleStyle = (role) => ROLE_COLORS[role] || ROLE_COLORS.operator;

  // Group customers by tenant
  const mmCustomers = customers.filter(c => c.tenant_group === 'MM');
  const mtCustomers = customers.filter(c => c.tenant_group === 'MT');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>Users</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{users.length} users total</p>
        </div>
        <div className="flex items-center gap-3">
          {isMasterAdmin && bulkSelectedUsers.length > 0 && (
            <button onClick={() => setShowBulkAssign(true)} data-testid="bulk-assign-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold"
              style={{ background: 'var(--warning-muted)', color: 'var(--warning)', border: '1px solid var(--warning)' }}>
              <Building2 size={16} /> Assign Customers ({bulkSelectedUsers.length})
            </button>
          )}
          <button onClick={() => setShowCreate(true)} data-testid="create-user-btn"
            className="btn-primary flex items-center gap-2 text-sm font-semibold">
            <Plus size={16} /> New User
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-slate overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin" size={28} style={{ color: 'var(--primary)' }} />
          </div>
        ) : (
          <table className="w-full table-comfortable" data-testid="users-table">
            <thead>
              <tr>
                {isMasterAdmin && <th className="w-10"></th>}
                {['Name', 'Email', 'Role', 'Access Scope', 'Customer', 'Status', 'Actions'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={isMasterAdmin ? 8 : 7} className="text-center py-20">
                    <Users size={32} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
                    <p style={{ color: 'var(--text-muted)' }}>No users found</p>
                  </td>
                </tr>
              ) : users.map((u) => {
                const customerName = u.customer_id ? customers.find(c => c.id === u.customer_id)?.company_name : null;
                const roleStyle = getRoleStyle(u.role);
                const isInternal = INTERNAL_ROLES.includes(u.role);
                const scopeBadge = ACCESS_SCOPE_BADGES[u.access_scope] || ACCESS_SCOPE_BADGES.ASSIGNED;
                const ScopeIcon = scopeBadge.icon;
                const assignedCount = u.allowed_customer_ids?.length || 0;
                const hasNoAccess = isInternal && u.access_scope === 'ASSIGNED' && assignedCount === 0 && u.role !== 'master_admin';
                
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className={hasNoAccess ? 'opacity-60' : ''}>
                    {isMasterAdmin && (
                      <td>
                        {isInternal && u.role !== 'master_admin' && (
                          <input type="checkbox" checked={bulkSelectedUsers.includes(u.id)}
                            onChange={() => toggleBulkSelect(u.id)}
                            className="w-4 h-4" style={{ accentColor: 'var(--primary)' }} />
                        )}
                      </td>
                    )}
                    <td>
                      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{u.full_name}</span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td>
                      <span className="inline-flex px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wide"
                        style={{ background: roleStyle.bg, color: roleStyle.text, border: `1px solid ${roleStyle.border}` }}>
                        {u.role?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      {isInternal ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide"
                            style={{ background: scopeBadge.bg, color: scopeBadge.text }}>
                            <ScopeIcon size={12} />
                            {u.access_scope === 'ALL' ? 'ALL' : `${assignedCount} assigned`}
                          </span>
                          {hasNoAccess && (
                            <AlertTriangle size={14} style={{ color: 'var(--warning)' }} title="No customers assigned - user cannot see any data" />
                          )}
                        </div>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--text-faint)' }}>—</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{customerName || '—'}</td>
                    <td>
                      <button onClick={() => toggleActive(u)} title={u.is_active ? 'Click to deactivate' : 'Click to activate'}
                        className="flex items-center gap-1.5 text-xs font-semibold uppercase transition-opacity hover:opacity-80"
                        style={{ color: u.is_active ? 'var(--success)' : 'var(--error)' }}>
                        {u.is_active ? <Shield size={14} /> : <ShieldOff size={14} />}
                        {u.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {isMasterAdmin && isInternal && u.role !== 'master_admin' && (
                          <button onClick={() => openAssign(u)} data-testid={`assign-user-${u.id}`}
                            className="p-2 rounded-md transition-colors"
                            style={{ border: '1px solid var(--warning)', color: 'var(--warning)' }}
                            title="Manage customer access">
                            <Building2 size={16} />
                          </button>
                        )}
                        <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                          className="p-2 rounded-md transition-colors"
                          style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)' }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)' }}
                          title="Edit user">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => openDelete(u)} data-testid={`delete-user-${u.id}`}
                          className="p-2 rounded-md transition-colors"
                          style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--error)'; e.currentTarget.style.color = 'var(--error)' }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)' }}
                          title="Delete user">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5 mt-2">
            {[
              { label: 'Full Name', key: 'full_name', type: 'text', required: true },
              { label: 'Email', key: 'email', type: 'email', required: true },
              { label: 'Password', key: 'password', type: 'password', required: true },
            ].map(({ label, key, type, required }) => (
              <div key={key}>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>{label}</label>
                <input type={type} required={required} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full input-slate" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Role</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value, access_scope: e.target.value === 'master_admin' ? 'ALL' : 'ASSIGNED' }))}
                className="w-full select-slate">
                {ROLES.map(r => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
            
            {/* Access Scope for Internal Roles */}
            {INTERNAL_ROLES.includes(form.role) && form.role !== 'master_admin' && isMasterAdmin && (
              <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--warning)' }}>
                  Tenant Access Scope
                </label>
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.access_scope === 'ASSIGNED'} 
                      onChange={() => setForm(p => ({ ...p, access_scope: 'ASSIGNED' }))}
                      className="w-4 h-4" style={{ accentColor: 'var(--warning)' }} />
                    <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Assigned Customers Only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.access_scope === 'ALL'} 
                      onChange={() => setForm(p => ({ ...p, access_scope: 'ALL' }))}
                      className="w-4 h-4" style={{ accentColor: 'var(--success)' }} />
                    <span className="text-sm" style={{ color: 'var(--text-muted)' }}>All Tenants (MM + MT)</span>
                  </label>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
                  {form.access_scope === 'ASSIGNED' 
                    ? 'User will only see customers assigned to them. You can assign customers after creation.'
                    : 'User will have access to ALL customers across MM and MT tenants.'}
                </p>
              </div>
            )}
            
            {form.role === 'customer' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Customer Account</label>
                <select value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))}
                  className="w-full select-slate">
                  <option value="">Select customer...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.company_name} ({c.tenant_group})</option>)}
                </select>
              </div>
            )}
            {form.role === 'vendor' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--warning)' }}>Vendor / Shop *</label>
                <select value={form.vendor_id} onChange={e => setForm(p => ({ ...p, vendor_id: e.target.value }))}
                  className="w-full select-slate"
                  data-testid="create-user-vendor-select"
                  required>
                  <option value="">Select shop...</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}{v.city ? ` — ${v.city}, ${v.us_state || ''}` : ''}</option>)}
                </select>
                <p className="text-[10px] mt-1.5 font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>
                  Vendor user only sees work orders where ops assigned this shop.
                </p>
              </div>
            )}
            <button type="submit" disabled={saving} data-testid="create-user-submit"
              className="w-full btn-primary flex items-center justify-center gap-2">
              {saving && <Loader2 size={16} className="animate-spin" />} Create User
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>Edit User</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-muted)' }}>{selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-5 mt-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Full Name</label>
              <input type="text" required value={editForm.full_name} onChange={e => setEditForm(p => ({ ...p, full_name: e.target.value }))}
                className="w-full input-slate" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Role</label>
              <select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
                className="w-full select-slate">
                {ROLES.map(r => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
            {editForm.role === 'customer' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Customer Account</label>
                <select value={editForm.customer_id} onChange={e => setEditForm(p => ({ ...p, customer_id: e.target.value }))}
                  className="w-full select-slate">
                  <option value="">Select customer...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
            )}
            {editForm.role === 'vendor' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--warning)' }}>Vendor / Shop *</label>
                <select value={editForm.vendor_id} onChange={e => setEditForm(p => ({ ...p, vendor_id: e.target.value }))}
                  className="w-full select-slate"
                  data-testid="edit-user-vendor-select">
                  <option value="">Select shop...</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}{v.city ? ` — ${v.city}, ${v.us_state || ''}` : ''}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Status</label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="status" checked={editForm.is_active === true} onChange={() => setEditForm(p => ({ ...p, is_active: true }))}
                    className="w-4 h-4" style={{ accentColor: 'var(--success)' }} />
                  <span style={{ color: 'var(--success)' }}>Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="status" checked={editForm.is_active === false} onChange={() => setEditForm(p => ({ ...p, is_active: false }))}
                    className="w-4 h-4" style={{ accentColor: 'var(--error)' }} />
                  <span style={{ color: 'var(--error)' }}>Inactive</span>
                </label>
              </div>
            </div>
            
            {/* Password Reset Section */}
            <div className="border-t pt-5" style={{ borderColor: 'var(--border)' }}>
              {!showPasswordField ? (
                <button type="button" onClick={() => setShowPasswordField(true)}
                  className="flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
                  style={{ color: 'var(--primary)' }}>
                  <Key size={16} /> Reset Password
                </button>
              ) : (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--warning)' }}>
                    New Password (min 6 characters)
                  </label>
                  <input type="password" value={editForm.new_password} 
                    onChange={e => setEditForm(p => ({ ...p, new_password: e.target.value }))}
                    placeholder="Leave blank to keep current password"
                    className="w-full input-slate"
                    style={{ borderColor: 'var(--warning)' }} />
                  <p className="text-xs mt-2" style={{ color: 'var(--text-faint)' }}>
                    ⚠️ User will be logged out of all sessions
                  </p>
                </div>
              )}
            </div>
            
            <button type="submit" disabled={saving} data-testid="edit-user-submit"
              className="w-full btn-primary flex items-center justify-center gap-2">
              {saving && <Loader2 size={16} className="animate-spin" />} Save Changes
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customer Assignment Dialog (Master Admin Only) */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-lg" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>
              Manage Customer Access
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--text-muted)' }}>
              {selectedUser?.full_name} ({selectedUser?.role})
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-5 mt-2">
            {/* Access Scope */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>
                Access Scope
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border-2 flex-1 transition-colors"
                  style={{ 
                    borderColor: editForm.access_scope === 'ASSIGNED' ? 'var(--warning)' : 'var(--border)',
                    background: editForm.access_scope === 'ASSIGNED' ? 'var(--warning-muted)' : 'var(--bg)'
                  }}>
                  <input type="radio" checked={editForm.access_scope === 'ASSIGNED'} 
                    onChange={() => setEditForm(p => ({ ...p, access_scope: 'ASSIGNED' }))}
                    className="w-4 h-4" style={{ accentColor: 'var(--warning)' }} />
                  <div>
                    <Lock size={14} style={{ color: 'var(--warning)' }} />
                    <span className="text-sm font-medium block mt-1" style={{ color: 'var(--text-primary)' }}>Assigned Only</span>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border-2 flex-1 transition-colors"
                  style={{ 
                    borderColor: editForm.access_scope === 'ALL' ? 'var(--success)' : 'var(--border)',
                    background: editForm.access_scope === 'ALL' ? 'var(--success-muted)' : 'var(--bg)'
                  }}>
                  <input type="radio" checked={editForm.access_scope === 'ALL'} 
                    onChange={() => setEditForm(p => ({ ...p, access_scope: 'ALL', allowed_customer_ids: [] }))}
                    className="w-4 h-4" style={{ accentColor: 'var(--success)' }} />
                  <div>
                    <Globe size={14} style={{ color: 'var(--success)' }} />
                    <span className="text-sm font-medium block mt-1" style={{ color: 'var(--text-primary)' }}>All Tenants</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Customer Multi-Select (only for ASSIGNED scope) */}
            {editForm.access_scope === 'ASSIGNED' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>
                  Assigned Customers ({editForm.allowed_customer_ids.length} selected)
                </label>
                
                {/* MM Customers */}
                {mmCustomers.length > 0 && (
                  <div className="mb-4">
                    <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2 flex items-center gap-2" 
                      style={{ color: 'rgb(16, 185, 129)' }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: 'rgb(16, 185, 129)' }}></span>
                      MM Customers
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 rounded-md" style={{ background: 'var(--bg)' }}>
                      {mmCustomers.map(c => (
                        <label key={c.id} className="flex items-center gap-2 cursor-pointer text-sm p-1.5 rounded hover:bg-white/5">
                          <input type="checkbox" 
                            checked={editForm.allowed_customer_ids.includes(c.id)}
                            onChange={e => {
                              if (e.target.checked) {
                                setEditForm(p => ({ ...p, allowed_customer_ids: [...p.allowed_customer_ids, c.id] }));
                              } else {
                                setEditForm(p => ({ ...p, allowed_customer_ids: p.allowed_customer_ids.filter(id => id !== c.id) }));
                              }
                            }}
                            className="w-4 h-4" style={{ accentColor: 'rgb(16, 185, 129)' }} />
                          <span style={{ color: 'var(--text-muted)' }}>{c.company_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* MT Customers */}
                {mtCustomers.length > 0 && (
                  <div>
                    <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2 flex items-center gap-2" 
                      style={{ color: 'rgb(139, 92, 246)' }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: 'rgb(139, 92, 246)' }}></span>
                      MT Customers
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 rounded-md" style={{ background: 'var(--bg)' }}>
                      {mtCustomers.map(c => (
                        <label key={c.id} className="flex items-center gap-2 cursor-pointer text-sm p-1.5 rounded hover:bg-white/5">
                          <input type="checkbox" 
                            checked={editForm.allowed_customer_ids.includes(c.id)}
                            onChange={e => {
                              if (e.target.checked) {
                                setEditForm(p => ({ ...p, allowed_customer_ids: [...p.allowed_customer_ids, c.id] }));
                              } else {
                                setEditForm(p => ({ ...p, allowed_customer_ids: p.allowed_customer_ids.filter(id => id !== c.id) }));
                              }
                            }}
                            className="w-4 h-4" style={{ accentColor: 'rgb(139, 92, 246)' }} />
                          <span style={{ color: 'var(--text-muted)' }}>{c.company_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {editForm.allowed_customer_ids.length === 0 && (
                  <div className="flex items-center gap-2 p-3 rounded-md mt-3" style={{ background: 'var(--warning-muted)', border: '1px solid var(--warning)' }}>
                    <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
                    <span className="text-sm" style={{ color: 'var(--warning)' }}>No customers assigned — user cannot see any data</span>
                  </div>
                )}
              </div>
            )}

            <button type="submit" disabled={saving} data-testid="assign-submit"
              className="w-full btn-primary flex items-center justify-center gap-2">
              {saving && <Loader2 size={16} className="animate-spin" />}
              <CheckCircle size={16} /> Save Access Settings
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssign} onOpenChange={setShowBulkAssign}>
        <DialogContent className="max-w-lg" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>
              Bulk Assign Customers
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--text-muted)' }}>
              Add customer access to {bulkSelectedUsers.length} selected users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>
                Select Customers to Add
              </label>
              <div className="max-h-64 overflow-y-auto p-2 rounded-md space-y-3" style={{ background: 'var(--bg)' }}>
                {/* MM Customers */}
                {mmCustomers.length > 0 && (
                  <div>
                    <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'rgb(16, 185, 129)' }}>MM</p>
                    {mmCustomers.map(c => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer text-sm p-1.5">
                        <input type="checkbox" 
                          checked={bulkCustomers.includes(c.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setBulkCustomers(p => [...p, c.id]);
                            } else {
                              setBulkCustomers(p => p.filter(id => id !== c.id));
                            }
                          }}
                          className="w-4 h-4" style={{ accentColor: 'rgb(16, 185, 129)' }} />
                        <span style={{ color: 'var(--text-muted)' }}>{c.company_name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {/* MT Customers */}
                {mtCustomers.length > 0 && (
                  <div>
                    <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'rgb(139, 92, 246)' }}>MT</p>
                    {mtCustomers.map(c => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer text-sm p-1.5">
                        <input type="checkbox" 
                          checked={bulkCustomers.includes(c.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setBulkCustomers(p => [...p, c.id]);
                            } else {
                              setBulkCustomers(p => p.filter(id => id !== c.id));
                            }
                          }}
                          className="w-4 h-4" style={{ accentColor: 'rgb(139, 92, 246)' }} />
                        <span style={{ color: 'var(--text-muted)' }}>{c.company_name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowBulkAssign(false); setBulkCustomers([]); }}
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                Cancel
              </Button>
              <Button onClick={handleBulkAssign} disabled={saving || bulkCustomers.length === 0}
                style={{ background: 'var(--primary)', color: 'white' }}>
                {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                Add to {bulkSelectedUsers.length} Users
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>Deactivate User</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-muted)' }}>
              Are you sure you want to deactivate <strong style={{ color: 'var(--text-primary)' }}>{selectedUser?.full_name}</strong>?
              <br /><br />
              This will prevent them from logging in. You can reactivate them later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDelete(false)}
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={saving}
              style={{ background: 'var(--error)', color: 'white' }}>
              {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
              Deactivate User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
